// Senhas de aplicativo.
//
// Existem por um motivo concreto: IMAP e SMTP não têm como pedir um segundo
// fator. Ligar o 2FA sem isto faria o Thunderbird e o app do celular pararem
// de entrar, sem explicação visível para quem usa.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as conta from "../api/conta";
import { explicar } from "../api/problema";
import { dataCurta } from "../ui/formato";
import { Aviso, Secao } from "./DoisFatores";

export function SenhasApp() {
  const fila = useQueryClient();
  const [descricao, setDescricao] = useState("");
  const [nova, setNova] = useState<{ descricao?: string; senha?: string } | null>(null);
  const [erro, setErro] = useState("");

  const lista = useQuery({ queryKey: ["conta", "senhas-app"], queryFn: () => conta.senhasApp() });

  const criar = useMutation({
    mutationFn: () => conta.criarSenhaApp(descricao),
    onSuccess: (d) => {
      setNova(d);
      setDescricao("");
      setErro("");
      void fila.invalidateQueries({ queryKey: ["conta", "senhas-app"] });
    },
    onError: (e) => setErro(explicar(e)),
  });

  const revogar = useMutation({
    mutationFn: (uuid: string) => conta.revogarSenhaApp(uuid),
    onSuccess: () => void fila.invalidateQueries({ queryKey: ["conta", "senhas-app"] }),
    onError: (e) => setErro(explicar(e)),
  });

  return (
    <Secao titulo="Senhas de aplicativo" icone="KeyRound">
      <p className="text-sm text-tinta-media">
        Uma senha por aparelho ou programa. Se um deles for perdido ou roubado,
        você revoga só aquela — sem trocar a senha da conta nem reconfigurar o
        resto.
      </p>

      {nova && (
        <div className="mt-4 rounded-lg border border-aviso/40 bg-aviso/10 p-4">
          <p className="text-sm text-tinta">
            Senha criada para <strong>{nova.descricao}</strong>. Ela aparece{" "}
            <strong>uma única vez</strong> — depois daqui, só existe o hash, e
            nem nós conseguimos mostrá-la de novo.
          </p>
          <p className="my-3 select-all break-all rounded border border-linha bg-white px-3 py-2 font-mono text-base tracking-wider">
            {nova.senha}
          </p>
          <button onClick={() => setNova(null)} className="text-sm text-acao underline">
            Já copiei
          </button>
        </div>
      )}

      <div className="mt-4 flex gap-2">
        <label htmlFor="descricao-senha" className="sr-only">
          Para qual aparelho
        </label>
        <input
          id="descricao-senha"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          placeholder="Thunderbird do escritório"
          className="flex-1 rounded-lg border border-linha px-3 py-2 text-sm"
        />
        <button
          onClick={() => criar.mutate()}
          disabled={!descricao.trim() || criar.isPending}
          className="rounded-lg bg-acao px-4 py-2 text-sm text-white disabled:opacity-60"
        >
          Criar
        </button>
      </div>

      {lista.data && lista.data.length > 0 && (
        <ul className="mt-4 divide-y divide-linha rounded-lg border border-linha">
          {lista.data.map((s) => (
            <li key={s.uuid} className="flex items-center gap-3 px-4 py-3 text-sm">
              <span className="flex-1 min-w-0">
                <span className="block truncate text-tinta">{s.descricao}</span>
                <span className="block text-xs text-tinta-fraca">
                  criada em {dataCurta(s.criado_em)}
                  {/* "nunca usada" é informação acionável: provavelmente é uma
                      senha esquecida, e a que se pode revogar sem medo. */}
                  {s.ultimo_uso
                    ? ` · último uso ${dataCurta(s.ultimo_uso)}`
                    : " · nunca usada"}
                </span>
              </span>
              <button
                onClick={() => revogar.mutate(s.uuid!)}
                className="text-xs text-perigo underline"
              >
                Revogar
              </button>
            </li>
          ))}
        </ul>
      )}

      {lista.data && lista.data.length === 0 && (
        <Aviso tom="neutro">
          Nenhuma criada. Se você usa só o webmail, não precisa de nenhuma.
        </Aviso>
      )}

      {erro && <p className="mt-3 text-sm text-perigo">{erro}</p>}
    </Secao>
  );
}
